import React, { useState } from 'react';
import { orderTrackingService } from '../../../services/orderTrackingService';
import Icon from '../../../components/AppIcon';

const CreateShipmentModal = ({ order, carriers, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    carrier: '',
    trackingNumber: '',
    shippingMethod: 'standard',
    estimatedDeliveryDate: '',
    shippingCost: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.carrier || !formData?.trackingNumber) {
      setError('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const selectedCarrier = carriers?.find(c => c?.carrierCode === formData?.carrier);
      const trackingUrl = selectedCarrier?.trackingUrlTemplate?.replace(
        '{tracking_number}',
        formData?.trackingNumber
      );

      await orderTrackingService?.createShipment({
        orderId: order?.id,
        carrier: formData?.carrier,
        trackingNumber: formData?.trackingNumber,
        carrierTrackingUrl: trackingUrl,
        shippingMethod: formData?.shippingMethod,
        estimatedDeliveryDate: formData?.estimatedDeliveryDate || null,
        shippingCost: parseFloat(formData?.shippingCost) || 0,
        deliveryAddress: order?.shippingAddress
      });

      onSuccess();
    } catch (err) {
      console.error('Error creating shipment:', err);
      setError(err?.message || 'Gönderi oluşturulurken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-1">
              Kargoya Ver
            </h2>
            <p className="text-sm text-muted-foreground">
              Sipariş #{order?.id?.substring(0, 8)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Order Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-foreground mb-2">Sipariş Bilgileri</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Tutar:</span>
                <span className="ml-2 font-medium">
                  {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' })?.format(order?.totalAmount)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Ürün:</span>
                <span className="ml-2 font-medium">{order?.items?.length} adet</span>
              </div>
              <div className="col-span-2">
                <span className="text-muted-foreground">Adres:</span>
                <span className="ml-2 font-medium">
                  {order?.shippingAddress?.address}, {order?.shippingAddress?.city}
                </span>
              </div>
            </div>
          </div>

          {/* Carrier Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Kargo Firması <span className="text-red-500">*</span>
            </label>
            <select
              value={formData?.carrier}
              onChange={(e) => setFormData({ ...formData, carrier: e?.target?.value })}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              required
            >
              <option value="">Kargo firması seçin</option>
              {carriers?.map((carrier) => (
                <option key={carrier?.id} value={carrier?.carrierCode}>
                  {carrier?.carrierName} - {carrier?.costPerKg} TL/kg
                </option>
              ))}
            </select>
          </div>

          {/* Tracking Number */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Takip Numarası <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData?.trackingNumber}
              onChange={(e) => setFormData({ ...formData, trackingNumber: e?.target?.value })}
              placeholder="Örn: 1234567890"
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          {/* Shipping Method */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Gönderim Yöntemi
            </label>
            <select
              value={formData?.shippingMethod}
              onChange={(e) => setFormData({ ...formData, shippingMethod: e?.target?.value })}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="standard">Standart Kargo</option>
              <option value="express">Hızlı Kargo</option>
              <option value="same_day">Aynı Gün Teslimat</option>
            </select>
          </div>

          {/* Estimated Delivery Date */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Tahmini Teslimat Tarihi
            </label>
            <input
              type="date"
              value={formData?.estimatedDeliveryDate}
              onChange={(e) => setFormData({ ...formData, estimatedDeliveryDate: e?.target?.value })}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Shipping Cost */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Kargo Ücreti (TL)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData?.shippingCost}
              onChange={(e) => setFormData({ ...formData, shippingCost: e?.target?.value })}
              placeholder="0.00"
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              İptal
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Icon name="Loader" size={18} className="animate-spin" />
                  <span>Oluşturuluyor...</span>
                </>
              ) : (
                <>
                  <Icon name="Check" size={18} />
                  <span>Kargoya Ver</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateShipmentModal;